import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import GoogleIcon from "@/components/google-icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AuthLayout from "@/layouts/auth-layout"

interface Props {
  user: { id: number; name: string; email: string }
  organization: { name: string; slug: string } | null
  token: string
}

export default function AcceptInvitation({ user, organization, token }: Props) {
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [submitting, setSubmitting] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    router.post(`/identity/invitations/confirm/${token}`, {
      password,
      password_confirmation: passwordConfirmation,
    })
  }

  // Accept the invite with Google instead of setting a password. The invited
  // User already exists, so the OAuth callback matches it by email and links +
  // signs in. login_hint nudges Google to the invited address. CSRF-protected
  // POST (omniauth-rails_csrf_protection), so it's a one-shot real form, not an
  // Inertia visit.
  function continueWithGoogle() {
    const csrf =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") ?? ""
    const form = document.createElement("form")
    form.method = "post"
    form.action = "/auth/google_oauth2"
    for (const [name, value] of [
      ["authenticity_token", csrf],
      ["login_hint", user.email],
    ]) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <AuthLayout
      title={`Welcome to ${organization?.name || "Rebulk"}`}
      description="Set your password to get started"
    >
      <Head title="Accept Invitation" />

      <div className="bg-muted/40 text-muted-foreground mb-6 rounded-lg border p-3 text-sm">
        <p className="text-foreground font-medium">{user.name}</p>
        <p>{user.email}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="grid gap-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            autoFocus
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="password_confirmation">Confirm Password</Label>
          <Input
            id="password_confirmation"
            type="password"
            required
            minLength={8}
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          disabled={submitting || password.length < 8}
          className="mt-2 w-full"
        >
          {submitting ? "Setting up..." : "Set Password & Sign In"}
        </Button>
      </form>

      <div className="mt-6 flex flex-col gap-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={continueWithGoogle}
        >
          <GoogleIcon className="size-4" />
          Continue with Google
        </Button>
      </div>
    </AuthLayout>
  )
}
